import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TouchableOpacity, View } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, Text } from '@components/base'
import { useGoBack } from '@src/utils/navigation'

interface UploadFileModalProps {
  onImagePress: () => void
  onPdfPress: () => void
}

export const UploadFileModal = ({ onImagePress, onPdfPress }: UploadFileModalProps) => {
  const styles = useStyleSheet(themedStyles)
  const goBack = useGoBack()

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isClosing, setIsClosing] = useState(false)

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
    },
    [],
  )

  const handleOptionPress = useCallback(
    (onPress: () => void) => {
      if (isClosing) {
        return
      }

      setIsClosing(true)
      onPress()

      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null
        goBack()
      }, 200)
    },
    [goBack, isClosing],
  )

  return (
    <View style={styles.container}>
      <Text type="title-2" style={styles.title}>
        Upload file
      </Text>

      <View style={styles.list}>
        <TouchableOpacity
          accessibilityLabel="Pick image"
          activeOpacity={0.8}
          disabled={isClosing}
          onPress={() => handleOptionPress(onImagePress)}
          style={styles.optionRow}
        >
          <Icon name="image" size={24} weight="bold" style={styles.optionIcon} />
          <Text type="large" style={styles.optionText}>
            Image
          </Text>
        </TouchableOpacity>

        <View style={styles.divider} />

        <TouchableOpacity
          accessibilityLabel="Pick PDF"
          activeOpacity={0.8}
          disabled={isClosing}
          onPress={() => handleOptionPress(onPdfPress)}
          style={styles.optionRow}
        >
          <Icon name="file-text" size={24} weight="bold" style={styles.optionIcon} />
          <Text type="large" style={styles.optionText}>
            PDF
          </Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        accessibilityLabel="Cancel upload"
        activeOpacity={0.8}
        disabled={isClosing}
        onPress={goBack}
        style={styles.cancelButton}
      >
        <Text type="large" style={styles.cancelText}>
          Cancel
        </Text>
      </TouchableOpacity>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    width: '100%',
    paddingHorizontal: 16,
  },
  title: {
    color: 'theme.text.primary',
    marginBottom: 20,
  },
  list: {
    backgroundColor: 'theme.background.modal',
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minHeight: 56,
  },
  optionIcon: {
    color: 'theme.primary.base',
  },
  optionText: {
    color: 'theme.text.primary',
  },
  divider: {
    height: 1,
    backgroundColor: 'theme.surface.base1',
  },
  cancelButton: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 56,
  },
  cancelText: {
    color: 'theme.text.primary',
  },
})
